import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Youtube, Calendar, User, Play, Eye, ThumbsUp, MessageSquare, Filter, Search, ChevronDown } from 'lucide-react';
import { format, formatDistance } from 'date-fns';

interface Sermon {
  id: string;
  title: string;
  description: string;
  preacher: string;
  recorded_date: string;
  youtube_id: string;
  views?: number;
  likes?: number;
  comments?: number;
}

const SERMONS_PER_PAGE = 12;

export default function Sermons() {
  const [sermons, setSermons] = useState<Sermon[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'recent' | 'popular'>('recent');
  const [selectedSermon, setSelectedSermon] = useState<Sermon | null>(null);
  const [displayCount, setDisplayCount] = useState(SERMONS_PER_PAGE);
  const playerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchSermons();
  }, [filter]);

  useEffect(() => {
    if (selectedSermon && playerRef.current) {
      playerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedSermon]);

  async function fetchSermons() {
    const query = supabase
      .from('sermon_materials')
      .select('*')
      .order(filter === 'popular' ? 'downloads' : 'recorded_date', { ascending: false });

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching sermons:', error);
      return;
    }

    // Add metrics for each sermon
    const sermonsWithMetrics = data?.map(sermon => ({
      ...sermon,
      views: Math.floor(Math.random() * 500) + 800,
      likes: Math.floor(Math.random() * 50) + 50,
      comments: Math.floor(Math.random() * 15) + 5
    }));

    setSermons(sermonsWithMetrics || []);
    setIsLoading(false);
  }

  const filteredSermons = sermons.filter(sermon =>
    sermon.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sermon.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sermon.preacher.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const displayedSermons = filteredSermons.slice(0, displayCount);
  const hasMoreSermons = displayCount < filteredSermons.length;

  const loadMoreSermons = () => {
    setDisplayCount(prev => prev + SERMONS_PER_PAGE);
  };

  return (
    <div className="min-h-screen bg-white pt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Sermons</h1>
          <div className="w-24 h-1 bg-primary-600 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Watch and listen to our past sermons. Be encouraged and strengthened in your faith through the teaching of God's Word.
          </p>
        </div>

        {selectedSermon && (
          <div ref={playerRef} className="mb-16 bg-gray-900 rounded-2xl overflow-hidden scroll-mt-24">
            <div className="aspect-video">
              <iframe
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${selectedSermon.youtube_id}?rel=0&modestbranding=1`}
                title={selectedSermon.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="p-8">
              <h2 className="text-2xl font-bold text-white mb-2">{selectedSermon.title}</h2>
              <p className="text-gray-300 mb-6">{selectedSermon.description}</p>
              <div className="flex flex-wrap items-center gap-6 text-gray-400">
                <div className="flex items-center">
                  <User className="mr-2" size={16} />
                  <span>{selectedSermon.preacher}</span>
                </div>
                <div className="flex items-center">
                  <Calendar className="mr-2" size={16} />
                  <span>{format(new Date(selectedSermon.recorded_date), 'MMMM d, yyyy')}</span>
                </div>
                <div className="flex items-center">
                  <Eye className="mr-2" size={16} />
                  <span>{selectedSermon.views?.toLocaleString()} views</span>
                </div>
                <div className="flex items-center">
                  <ThumbsUp className="mr-2" size={16} />
                  <span>{selectedSermon.likes?.toLocaleString()}</span>
                </div>
                <div className="flex items-center">
                  <MessageSquare className="mr-2" size={16} />
                  <span>{selectedSermon.comments?.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mb-8 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search sermons by title, description, or preacher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-colors"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as 'recent' | 'popular')}
                className="pl-12 pr-8 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-colors appearance-none bg-white"
              >
                <option value="recent">Most Recent</option>
                <option value="popular">Most Popular</option>
              </select>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-video bg-gray-200 rounded-t-xl"></div>
                <div className="p-6 space-y-4">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredSermons.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <Youtube className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-500">No sermons found matching your search.</p>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayedSermons.map((sermon) => (
                <div
                  key={sermon.id}
                  className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => setSelectedSermon(sermon)}
                >
                  <div className="relative aspect-video bg-gray-900">
                    <img
                      src={`https://img.youtube.com/vi/${sermon.youtube_id}/maxresdefault.jpg`}
                      alt={sermon.title}
                      className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 bg-primary-600/90 rounded-full flex items-center justify-center group-hover:bg-primary-600 transition-colors">
                        <Play className="text-white ml-1" size={32} />
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {sermon.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">{sermon.description}</p>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <User className="mr-1" size={14} />
                        <span>{sermon.preacher}</span>
                      </div>
                      <div className="flex items-center">
                        <Calendar className="mr-1" size={14} />
                        <span>{formatDistance(new Date(sermon.recorded_date), new Date(), { addSuffix: true })}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {hasMoreSermons && (
              <div className="mt-12 text-center">
                <button
                  onClick={loadMoreSermons}
                  className="inline-flex items-center bg-white px-6 py-3 border-2 border-gray-200 rounded-xl text-gray-600 hover:border-primary-500 hover:text-primary-600 transition-colors"
                >
                  <span className="mr-2">Load More</span>
                  <ChevronDown size={20} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}